import { useCallback, useMemo } from "react";

import { FilterId, FilterType } from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useMROIStore } from "@/pages/channel-roi/store/useMROIStore";

export const useAvailableFilters = () => {
  const { setTargetNodeUUID, setFunnelStage, setChannels, setGeographies } = useMROIStore();
  const { targetNodeUUID, funnelStage, channels, geographies } = useMROIStore(
    (state) => state.filters
  );

  const { initInfo: info } = useInfo();
  const [geoData] = info?.geographies || [];

  const handleTargetNodeChange = useCallback(
    (targetNode: string | null) => {
      setTargetNodeUUID(targetNode);
    },
    [setTargetNodeUUID]
  );

  const handleFunnelStageChange = useCallback(
    (funnelStage: string | null) => setFunnelStage(funnelStage),
    [setFunnelStage]
  );

  const handleChannelsChange = useCallback(
    (channel: string[]) => setChannels(channel),
    [setChannels]
  );

  const handleGeographiesChange = useCallback(
    (geography: string[]) => setGeographies(geography),
    [setGeographies]
  );

  // Memoize each filter option separately
  const targetNodeFilter = useMemo(
    () => ({
      id: FilterId.BusinessUnit,
      label: "Business Unit",
      type: FilterType.BusinessUnit,
      value: targetNodeUUID,
      onChange: handleTargetNodeChange,
    }),
    [targetNodeUUID, handleTargetNodeChange]
  );

  const funnelStageFilter = useMemo(
    () => ({
      id: FilterId.Funnel,
      label: "Funnels",
      type: FilterType.SingleSelectWithAll,
      value: funnelStage,
      onChange: handleFunnelStageChange,
    }),
    [funnelStage, handleFunnelStageChange]
  );

  const channelsFilter = useMemo(
    () => ({
      id: FilterId.Channel,
      label: "Channels",
      type: FilterType.MultiSelect,
      value: channels,
      onChange: handleChannelsChange,
    }),
    [channels, handleChannelsChange]
  );

  const geographiesFilter = useMemo(
    () => ({
      id: FilterId.Geography,
      label: "Geo",
      type: FilterType.MultiSelect,
      value: geographies,
      onChange: handleGeographiesChange,
    }),
    [geographies, handleGeographiesChange]
  );

  return useMemo(() => {
    const filters = [targetNodeFilter, funnelStageFilter, channelsFilter];
    if (geoData && geoData.length > 0) {
      filters.push(geographiesFilter);
    }
    return filters;
  }, [targetNodeFilter, funnelStageFilter, channelsFilter, geographiesFilter, geoData]);
};
